import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useReactToPrint } from 'react-to-print';
import { FiUserPlus, FiShare2 as FiUpload, FiFileText as FiPrinter } from 'react-icons/fi';
import MvrLogo from '../../assets/images/mvr.png';
import '../../styles/Voice.css';
import './mvr-print.css';
import { useToast } from '../../context/ToastContext';

const getToken      = () => localStorage.getItem('toolboxToken') || '';
const getNcToken    = () => localStorage.getItem('amsToken')     || '';

export interface PersonDetails {
  firstName?: string; middleName?: string; lastName?: string;
  gender?: string; eyeColor?: string; hairColor?: string; age?: string | number; weight?: string | number;
  birthDate?: string; licenseNumber?: string; licenseState?: string;
}
export interface AddressDetails { street?: string; city?: string; state?: string; }
export interface ViolationDetail {
  date?: string; convictionDate?: string; state?: string; code?: string;
  description?: string; location?: string; speed?: string; acd?: string; avd?: string;
}
export interface AccidentDetail {
  date?: string; code?: string; description?: string; location?: string; acd?: string; avd?: string;
}
export interface LicenseDetail {
  issueDate?: string; expirationDate?: string; class?: string; classDescription?: string;
  status?: string; restrictions?: string; cdlMedicalType?: string; cdlMedicalExpirationDate?: string; type?: string;
}

interface Props {
  transactionId: string;
  clientId: string;
  clientName: string;
  personDetails: PersonDetails;
  addressDetails: AddressDetails;
  violationsDetails: ViolationDetail[];
  accidentsDetails: AccidentDetail[];
  licenseDetails: LicenseDetail[];
  isDriverAdded: boolean;
  isViolationAdded: boolean;
  onWriteBackUpdated?: (isDriverAdded: boolean, isViolationAdded: boolean) => void;
}

const MVRDetailsReport: React.FC<Props> = ({
  transactionId, clientId, clientName, personDetails, addressDetails,
  violationsDetails, accidentsDetails, licenseDetails,
  isDriverAdded, isViolationAdded, onWriteBackUpdated,
}) => {
  const navigate = useNavigate();
  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ contentRef: printRef });
  const [addingDriver, setAddingDriver]       = useState(false);
  const [addingViolation, setAddingViolation] = useState(false);
  const [uploadingFile, setUploadingFile]     = useState(false);
  const [driverId, setDriverId]               = useState('');
  const { showSuccess, showError } = useToast();

  const updateHistory = (driverAdded: boolean, violationAdded: boolean, requestBody: any, responseBody: any) => {
    axios.post(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/mvr/update`, {
      transactionId,
      isDriverAdded: driverAdded,
      isViolationAdded: violationAdded,
      requestJson: JSON.stringify(requestBody),
      responseJson: JSON.stringify(responseBody),
    }, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(() => onWriteBackUpdated?.(driverAdded, violationAdded))
      .catch(() => {});
  };

  const addDriverData = () => {
    setAddingDriver(true);
    const requestDataList = {
      insured_database_id: clientId,
      first_name: personDetails.firstName ?? '',
      last_name: personDetails.lastName ?? '',
      insured_commercial_name: clientName,
      license_number: personDetails.licenseNumber ?? '',
      date_of_birth: personDetails.birthDate ?? '',
      gender: personDetails.gender ?? '',
      license_state: personDetails.licenseState ?? '',
      active: true,
      address_line_1: addressDetails.street ?? '',
      driver_address_state: addressDetails.state ?? '',
      city: addressDetails.city ?? '',
    };
    axios.post(`${(process.env.REACT_APP_NOWCERTSAPI      || '')}/api/nowcert/adddriver`, requestDataList, {
      params: { ncToken: getNcToken() },
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => {
        setDriverId(res.data?.databaseId || '');
        updateHistory(true, false, requestDataList, res.data);
        setAddingDriver(false);
        showSuccess('Driver added successfully.');
      })
      .catch(() => { setAddingDriver(false); showError('Error while adding driver data in NowCerts.'); });
  };

  const insertViolationData = () => {
    setAddingViolation(true);
    const driverViolations = violationsDetails.map(v => ({
      violationCode: v.code ?? '',
      violationDate: v.date ?? '',
      description: v.description ?? '',
    }));
    const violationRequests = { driverId, driverViolations };
    axios.post(`${(process.env.REACT_APP_NOWCERTSAPI      || '')}/api/nowcert/insertviolation`, violationRequests, {
      params: { ncToken: getNcToken() },
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => {
        setAddingViolation(false);
        if (res.data?.status === 1) {
          updateHistory(true, true, violationRequests, res.data);
          showSuccess('Violation added successfully.');
        } else {
          showError(res.data?.[0]?.message || 'Error while adding violation data in NowCerts.');
        }
      })
      .catch(() => { setAddingViolation(false); showError('Error while adding violation data in NowCerts.'); });
  };

  const uploadFileToAMS = async () => {
    setUploadingFile(true);
    try {
      const element = printRef.current;
      if (!element) throw new Error('Print element not found');

      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        ignoreElements: (el: Element) => el.classList.contains('mvr-no-print'),
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const pdfBase64 = pdf.output('dataurlstring').split(',')[1];

      const uploadData = {
        TransactionId: transactionId,
        FileName: `MVR_${new Date().getTime()}.pdf`,
        NCInsuredId: clientId,
        FolderName: 'MVRs',
        FileData: pdfBase64,
        NowCertsToken: getNcToken(),
        NCPolicyId: '',
      };

      await axios.post(`${(process.env.REACT_APP_NOWCERTSAPI      || '')}/api/nowcert/uploadmvr`, uploadData, {
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      });

      showSuccess('File uploaded to AMS successfully.');
      setUploadingFile(false);
    } catch (error: any) {
      setUploadingFile(false);
      if (error.response != null) {
        if (error.response.status === 401) navigate('/');
        else if (error.response.status === 400) showError('Bad Request');
        else showError('Unknown error while uploading file to AMS.');
      } else {
        showError(error.message || 'Error generating PDF.');
      }
    }
  };

  const cardBg = '#ffffff';
  const border = '1px solid #e5e7eb';
  const cell: React.CSSProperties = { padding: '10px 14px', fontSize: 13, color: '#374151', border, background: '#ffffff' };
  const label: React.CSSProperties = { ...cell, color: '#6b7280', fontWeight: 600, width: '20%', background: '#f9fafb' };
  const th: React.CSSProperties = { padding: '10px 14px', fontSize: 12, fontWeight: 700, textAlign: 'left', color: '#374151', background: '#f3f4f6', border };
  const td: React.CSSProperties = { padding: '10px 14px', fontSize: 13, color: '#374151', border };
  const sectionTitle: React.CSSProperties = { fontSize: 14, fontWeight: 700, color: '#1f2937', margin: '22px 0 10px', paddingBottom: 6, borderBottom: '1px solid #e5e7eb' };

  return (
    <>
      <div ref={printRef} style={{ background: cardBg, color: '#1f2937', borderRadius: 10, padding: '20px 24px' }}>
      {/* Print / Add File In AMS */}
      <div className="mvr-no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 12 }}>
        <button className="cs-browse-btn" onClick={() => handlePrint()} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <FiPrinter size={13} /> Print
        </button>
        <button className="cs-browse-btn" disabled={uploadingFile} onClick={uploadFileToAMS} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {uploadingFile ? <span className="cs-upload-spinner" /> : <><FiUpload size={13} /> Add File In AMS</>}
        </button>
      </div>

      {/* Letterhead */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <img src={MvrLogo} alt="MVR" width={30} height={30} />
        <span style={{ fontSize: 16, fontWeight: 700, color: '#1f2937' }}>Motor Vehicle Record</span>
      </div>

      <p style={sectionTitle}>Personal Information</p>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td style={label}>Name</td>
            <td style={cell}>{`${personDetails.firstName ?? ''} ${personDetails.middleName ?? ''} ${personDetails.lastName ?? ''}`.trim() || 'N/A'}</td>
            <td style={label}>Sex</td>
            <td style={cell}>{personDetails.gender ?? 'N/A'}</td>
          </tr>
          <tr>
            <td style={label}>Address</td>
            <td style={cell}>{[addressDetails.street, addressDetails.city, addressDetails.state].filter(Boolean).join(', ') || 'N/A'}</td>
            <td style={label}>Eyes</td>
            <td style={cell}>{personDetails.eyeColor ?? 'N/A'}</td>
          </tr>
          <tr>
            <td style={label}>Age</td>
            <td style={cell}>{personDetails.age ?? 'N/A'}</td>
            <td style={label}>Hair</td>
            <td style={cell}>{personDetails.hairColor ?? 'N/A'}</td>
          </tr>
          <tr>
            <td style={label}>Weight</td>
            <td style={cell}>{personDetails.weight ?? 'N/A'}</td>
            <td style={label}>Date of Birth</td>
            <td style={cell}>{personDetails.birthDate ?? 'N/A'}</td>
          </tr>
          <tr>
            <td style={label}>License Number</td>
            <td style={cell}>{personDetails.licenseNumber ?? 'N/A'}</td>
            <td style={label}>License State</td>
            <td style={cell}>{personDetails.licenseState ?? 'N/A'}</td>
          </tr>
        </tbody>
      </table>

      {!isDriverAdded && personDetails.firstName && personDetails.lastName && personDetails.licenseNumber && (
        <div className="mvr-no-print" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
          <button className="cs-browse-btn" disabled={addingDriver} onClick={addDriverData} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {addingDriver ? <span className="cs-upload-spinner" /> : <><FiUserPlus size={13} /> Add Driver</>}
          </button>
        </div>
      )}

      <p style={sectionTitle}>Violations</p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Date', 'Conviction Date', 'State', 'Code', 'Description', 'Location', 'Speed', 'ACD', 'AVD'].map(h => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {violationsDetails.length > 0 ? violationsDetails.map((v, i) => (
              <tr key={i}>
                <td style={td}>{v.date ?? 'N/A'}</td>
                <td style={td}>{v.convictionDate ?? 'N/A'}</td>
                <td style={td}>{v.state ?? 'N/A'}</td>
                <td style={td}>{v.code ?? 'N/A'}</td>
                <td style={td}>{v.description ?? 'N/A'}</td>
                <td style={td}>{v.location ?? 'N/A'}</td>
                <td style={td}>{v.speed ?? 'N/A'}</td>
                <td style={td}>{v.acd ?? 'N/A'}</td>
                <td style={td}>{v.avd ?? 'N/A'}</td>
              </tr>
            )) : (
              <tr><td style={{ ...td, textAlign: 'center' }} colSpan={9}>No Violations</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {violationsDetails.length > 0 && isDriverAdded && !isViolationAdded && (
        <div className="mvr-no-print" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
          <button className="cs-browse-btn" disabled={addingViolation} onClick={insertViolationData} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {addingViolation ? <span className="cs-upload-spinner" /> : 'Add Violation'}
          </button>
        </div>
      )}

      <p style={sectionTitle}>Accidents</p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Date', 'Code', 'Description', 'Location', 'ACD', 'AVD'].map(h => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {accidentsDetails.length > 0 ? accidentsDetails.map((a, i) => (
              <tr key={i}>
                <td style={td}>{a.date ?? 'N/A'}</td>
                <td style={td}>{a.code ?? 'N/A'}</td>
                <td style={td}>{a.description ?? 'N/A'}</td>
                <td style={td}>{a.location ?? 'N/A'}</td>
                <td style={td}>{a.acd ?? 'N/A'}</td>
                <td style={td}>{a.avd ?? 'N/A'}</td>
              </tr>
            )) : (
              <tr><td style={{ ...td, textAlign: 'center' }} colSpan={6}>No Accidents</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p style={sectionTitle}>License Information</p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Issue Date', 'Expire Date', 'Class', 'Class Description', 'Status', 'Restriction', 'CDL Type', 'CDL Expiration Date', 'Type'].map(h => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {licenseDetails.length > 0 ? licenseDetails.map((l, i) => (
              <tr key={i}>
                <td style={td}>{l.issueDate ?? 'N/A'}</td>
                <td style={td}>{l.expirationDate ?? 'N/A'}</td>
                <td style={td}>{l.class ?? 'N/A'}</td>
                <td style={td}>{l.classDescription ?? 'N/A'}</td>
                <td style={td}>{l.status ?? 'N/A'}</td>
                <td style={td}>{l.restrictions ?? 'N/A'}</td>
                <td style={td}>{l.cdlMedicalType ?? 'N/A'}</td>
                <td style={td}>{l.cdlMedicalExpirationDate ?? 'N/A'}</td>
                <td style={td}>{l.type ?? 'N/A'}</td>
              </tr>
            )) : (
              <tr><td style={{ ...td, textAlign: 'center' }} colSpan={9}>No License Information</td></tr>
            )}
          </tbody>
        </table>
      </div>
      </div>
    </>
  );
};

export default MVRDetailsReport;

